import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from 'src/components/Toggle/Toggle';
import { getSharedLinkDocumentSettings, SharedLinkDocumentGroup, SharedLinkDocumentSettings, updateSharedLinkDocumentSettings } from 'src/services/CustomerService';
import WalToast from 'src/utils/WalToast';

/**
 * Settings > Shared Tracking Link (cp-meeting-changes F3): which document groups people with the
 * customer's tracking link can download. All off until an admin turns one on; every portal user
 * sees the list, only admins can change it (the API enforces the same). Each toggle saves on its
 * own and rolls back with an error toast if the save fails.
 */
const SharedLinkDocumentsSection = () => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState<SharedLinkDocumentSettings | null>(null);
    const [groups, setGroups] = useState<SharedLinkDocumentGroup[]>([]);
    const [canEdit, setCanEdit] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [savingGroup, setSavingGroup] = useState<SharedLinkDocumentGroup | null>(null);

    useEffect(() => {
        let isMounted = true;

        getSharedLinkDocumentSettings()
            .then((response: any) => {
                if (!isMounted) return;
                const data = response?.data ?? {};
                setSettings(data.settings ?? null);
                setGroups(Array.isArray(data.availableGroups) ? data.availableGroups : []);
                setCanEdit(data.canEdit === true);
            })
            .catch(() => isMounted && WalToast.error(t('SharedLinkSettings.Load_Failed')))
            .finally(() => isMounted && setIsLoading(false));

        return () => {
            isMounted = false;
        };
    }, [t]);

    const handleToggle = async (group: SharedLinkDocumentGroup) => {
        if (!settings || !canEdit || savingGroup) return;

        const previous = settings;
        const next = { ...settings, [group]: !settings[group] };
        setSettings(next);
        setSavingGroup(group);

        try {
            await updateSharedLinkDocumentSettings(next);
        } catch {
            setSettings(previous);
            WalToast.error(t('SharedLinkSettings.Save_Failed'));
        } finally {
            setSavingGroup(null);
        }
    };

    return (
        <>
            <div className="pb-3 border-b border-utilityGray200 sticky bg-inherit top-0 z-[3] stepper-content-header">
                <div className="flex flex-col">
                    <h6 className="text-grayLight900 text-base font-semibold">{t('SharedLinkSettings.Section_Title')}</h6>
                    <p className="text-grayLight600 text-xs leading-[1.5] font-normal">{t('SharedLinkSettings.Section_Desc')}</p>
                </div>
            </div>

            <div className="pt-4 flex flex-col gap-3">
                {!isLoading && !canEdit && <p className="text-xs leading-[1.5] font-normal text-grayLight600">{t('SharedLinkSettings.Admin_Only')}</p>}

                <ul className="flex flex-col border border-utilityGray200 rounded-[10px] bg-gray25 px-3 divide-y divide-utilityGray200">
                    {(isLoading ? (['bol', 'pod', 'customerInvoice'] as SharedLinkDocumentGroup[]) : groups).map((group) => (
                        <li key={group} className="flex items-start justify-between gap-3 py-3">
                            <div className={`flex flex-col ${isLoading ? 'custom-loading' : ''}`}>
                                <p className="text-[12px] leading-[18px] font-semibold text-textSecondary">{t(`SharedLinkSettings.Group_${group}`)}</p>
                                <p className="text-[12px] leading-[18px] font-normal text-grayLight600">{t(`SharedLinkSettings.Group_${group}_Desc`)}</p>
                            </div>
                            <Toggle
                                id={`shared-link-${group}`}
                                isChecked={settings?.[group] === true}
                                isDisabled={isLoading || !canEdit || savingGroup !== null}
                                onChange={() => handleToggle(group)}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
};

export default SharedLinkDocumentsSection;
